import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];
const STATUSES = ["todo", "in-progress", "done"];

export default function TasksTab({ eventId, members, canEdit }) {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assignee_member_id: "", due_date: "", priority: "medium", status: "todo" });

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/events/${eventId}/tasks`); setTasks(data); }
    catch (e) { toast.error("Could not load tasks"); }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.title) { toast.error("Add a title"); return; }
    try {
      await api.post(`/events/${eventId}/tasks`, { ...form, assignee_member_id: form.assignee_member_id || null, due_date: form.due_date || null });
      setOpen(false);
      setForm({ title: "", description: "", assignee_member_id: "", due_date: "", priority: "medium", status: "todo" });
      load();
      toast.success("Task added");
    } catch (e) { toast.error("Could not add"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this task?")) return;
    try { await api.delete(`/events/${eventId}/tasks/${id}`); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  const toggle = async (task) => {
    const next = task.status === "done" ? "todo" : "done";
    try {
      await api.patch(`/events/${eventId}/tasks/${task.id}`, { status: next });
      setTasks(tasks.map(t => t.id === task.id ? { ...t, status: next } : t));
    } catch (e) { toast.error("Could not update"); }
  };

  const visible = tasks.filter(t => filter === "all" || t.status === filter);
  const done = tasks.filter(t => t.status === "done").length;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2">MOD.TASKS / CHECKLIST</div>
          <h2 className="font-display text-xl md:text-2xl font-bold text-app tracking-tight leading-none">
            <span className="font-mono tabular-nums">{done.toString().padStart(2, "0")}</span> of <span className="font-mono tabular-nums">{tasks.length.toString().padStart(2, "0")}</span> complete
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {["all", "todo", "in-progress", "done"].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`task-filter-${s}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm ${filter === s ? "bevel-btn-primary text-white" : "text-muted hover:bg-surface-2"}`}
            >{s}</button>
          ))}
          {canEdit && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-task-btn" className="rounded-sm bg-app hover:bg-[#000000]">
                  <Plus size={16} strokeWidth={1.5} className="mr-2" /> Add task
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-surface rounded-sm max-w-lg">
                <DialogHeader><DialogTitle className="font-serif text-2xl text-app">New task</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <F label="Title"><Input data-testid="task-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></F>
                  <F label="Details"><Textarea rows={2} className="rounded-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></F>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Assignee">
                      <Select value={form.assignee_member_id || "none"} onValueChange={(v) => setForm({ ...form, assignee_member_id: v === "none" ? "" : v })}>
                        <SelectTrigger className="rounded-sm"><SelectValue placeholder="Me" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Me (planner)</SelectItem>
                          {members.map(m => <SelectItem key={m.id} value={m.id}>{m.name} · {m.role}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </F>
                    <F label="Due"><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></F>
                  </div>
                  <F label="Priority">
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button data-testid="task-submit" onClick={add} className="rounded-sm bg-app hover:bg-[#000000]">Add task</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="bevel border-hair rounded-sm divide-y divide-[color:var(--hair)]/60">
        {visible.length === 0 && <div className="p-10 text-center text-muted font-light">No tasks here.</div>}
        {visible.map(t => {
          const assignee = members.find(m => m.id === t.assignee_member_id);
          const done = t.status === "done";
          return (
            <div key={t.id} data-testid={`task-row-${t.id}`} className="p-4 flex items-start gap-4 hover:bg-canvas/40 transition-colors">
              <Checkbox
                data-testid={`task-check-${t.id}`}
                checked={done}
                onCheckedChange={() => toggle(t)}
                className="mt-1 rounded-sm border-hair-strong data-[state=checked]:bg-app data-[state=checked]:border-[#0A0A0A]"
              />
              <div className="flex-1 min-w-0">
                <div className={`font-medium ${done ? "line-through text-muted" : "text-app"}`}>{t.title}</div>
                {t.description && <div className="text-sm text-muted font-light mt-0.5">{t.description}</div>}
                <div className="flex items-center gap-3 mt-2 text-xs">
                  {assignee ? (
                    <span className="text-muted">→ {assignee.name}</span>
                  ) : <span className="text-muted">→ Planner</span>}
                  {t.due_date && <span className="text-muted">· due {new Date(t.due_date).toLocaleDateString(undefined, { month:"short", day:"numeric"})}</span>}
                  <span className={`px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${
                    t.priority === "high" ? "bg-[#EF4444]/10 text-[#EF4444]" :
                    t.priority === "medium" ? "bg-[#F59E0B]/15 text-[#F59E0B]" :
                    "bg-surface-2 text-[#059669]"
                  }`}>{t.priority}</span>
                </div>
              </div>
              {canEdit && (
                <div className="flex items-center gap-2">
                  <Select value={t.status} onValueChange={async (v) => {
                    try { await api.patch(`/events/${eventId}/tasks/${t.id}`, { status: v }); setTasks(tasks.map(x => x.id === t.id ? { ...x, status: v } : x)); }
                    catch (e) { toast.error("Could not update"); }
                  }}>
                    <SelectTrigger className="w-32 h-8 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <button onClick={() => del(t.id)} data-testid={`delete-task-${t.id}`} className="text-muted hover:text-[#EF4444] p-1"><Trash2 size={14} strokeWidth={1.5} /></button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (<div><Label className="eyebrow block mb-2">{label}</Label>{children}</div>);
}
